import { useEffect, useState } from "react";
import { authClient } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import { Switch } from "@/components/ui/switch";
import { sfx } from "@/lib/sounds";
import { toast } from "sonner";
import { Bell, Volume2, Monitor, Play } from "lucide-react";

const NOTIF_TYPES = [
  { key: "message", label: "Messages", default_sound: "chime_message" },
  { key: "mention", label: "Mentions", default_sound: "chime_mention" },
  { key: "follow", label: "Followers / Friend requests", default_sound: "chime_follow" },
  { key: "like", label: "Likes", default_sound: "chime_community" },
  { key: "comment", label: "Comments", default_sound: "chime_community" },
  { key: "approved", label: "Approvals (creator / event)", default_sound: "chime_approved" },
  { key: "rejected", label: "Rejections", default_sound: "no" },
  { key: "system", label: "System / Announcements", default_sound: "chime_announcement" },
];

const SOUND_OPTIONS = [
  "chime_message", "chime_mention", "chime_follow", "chime_community",
  "chime_event", "chime_approved", "chime_creator", "chime_announcement",
  "pling", "levelup", "no",
];

export default function NotificationPrefs() {
  const rt = useRealtime();
  const [prefs, setPrefs] = useState({
    master_enabled: true, browser_enabled: false, sounds_enabled: true,
    types: {},
  });
  const [busy, setBusy] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    authClient.get("/auth/notification-prefs").then((r) => {
      const cur = r.data.notification_prefs || {};
      setPrefs({
        master_enabled: cur.master_enabled ?? true,
        browser_enabled: cur.browser_enabled ?? false,
        sounds_enabled: cur.sounds_enabled ?? true,
        types: cur.types || {},
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await authClient.put("/auth/notification-prefs", prefs);
      rt.setPrefs(prefs);
      sfx.success();
      toast.success("Notification preferences saved");
    } catch (e) {
      sfx.error();
      toast.error("Save failed");
    }
    setBusy(false);
  };

  const requestBrowser = async () => {
    if (!("Notification" in window)) return toast.error("Browser notifications not supported");
    try {
      const r = await Notification.requestPermission();
      setPermission(r);
      if (r === "granted") {
        setPrefs({ ...prefs, browser_enabled: true });
        toast.success("Browser notifications enabled");
      } else {
        toast.error("Permission denied");
      }
    } catch {
      toast.error("Failed to request permission");
    }
  };

  const setType = (key, patch) => setPrefs({
    ...prefs,
    types: { ...prefs.types, [key]: { ...(prefs.types[key] || {}), ...patch } },
  });

  const testSound = (name) => { try { sfx[name]?.(); } catch { /* ignore */ } };

  return (
    <div className="max-w-3xl space-y-6" data-testid="notification-prefs">
      {/* Master toggles */}
      <div className="rounded-2xl glass p-6 space-y-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">
          <Bell size={13} /> Master Controls
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Enable notifications</span>
          <Switch checked={!!prefs.master_enabled} onCheckedChange={(v) => setPrefs({ ...prefs, master_enabled: v })} data-testid="notif-master" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-2"><Volume2 size={14} /> Sound alerts</span>
          <Switch checked={!!prefs.sounds_enabled} onCheckedChange={(v) => setPrefs({ ...prefs, sounds_enabled: v })} data-testid="notif-sounds" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-2"><Monitor size={14} /> Browser / Desktop pop-ups
            {permission === "granted" && <span className="text-[10px] text-emerald-400">(permission granted)</span>}
            {permission === "denied" && <span className="text-[10px] text-red-400">(permission denied — enable in browser settings)</span>}
          </span>
          <div className="flex items-center gap-2">
            {permission !== "granted" && permission !== "denied" && (
              <button onClick={requestBrowser} data-testid="notif-browser-request"
                      className="px-3 py-1.5 rounded-full border border-[#FFBF00]/40 text-[#FFBF00] text-[10px] uppercase tracking-widest">Enable</button>
            )}
            <Switch checked={!!prefs.browser_enabled} onCheckedChange={(v) => setPrefs({ ...prefs, browser_enabled: v })} data-testid="notif-browser" />
          </div>
        </div>
      </div>

      {/* Per-type */}
      <div className="rounded-2xl glass p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-4">
          Per-type Preferences
        </div>
        <div className="divide-y divide-white/5">
          {NOTIF_TYPES.map(({ key, label, default_sound }) => {
            const cfg = prefs.types[key] || {};
            const enabled = cfg.enabled ?? true;
            const sound = cfg.sound || default_sound;
            return (
              <div key={key} className="py-3 flex items-center gap-3" data-testid={`notif-type-${key}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold">{label}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-widest">Type · {key}</div>
                </div>
                <select value={sound} onChange={(e) => setType(key, { sound: e.target.value })}
                        data-testid={`notif-sound-${key}`}
                        className="bg-black/60 border border-white/10 rounded-md px-2 py-1.5 text-xs">
                  {SOUND_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => testSound(sound)} data-testid={`notif-preview-${key}`} className="p-1.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40" title="Preview"><Play size={11} /></button>
                <Switch checked={!!enabled} onCheckedChange={(v) => setType(key, { enabled: v })} data-testid={`notif-toggle-${key}`} />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="notif-save"
                className="px-6 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold disabled:opacity-60">
          Save Preferences
        </button>
        <div className="text-xs text-white/40">
          {rt.connected ? <span className="text-emerald-400">● Real-time connected</span> : <span>○ Realtime offline</span>}
        </div>
      </div>
    </div>
  );
}
